import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment-timezone";
import { ERROR_STATUS_KEY } from "constant/errorCode";
import { extractValidationErrorMessage } from "helpers/common/apiErrorHelper";
import {
  confirmAddOnPayment,
  getAvailableAddOns,
  orderAddOns,
  quoteAddOns,
} from "services/userManagementAPIs";
import { useAuth } from "hooks‬/Auth";
import { useCommonContext } from "hooks‬/CommonContext";
import { useFeatureAccess } from "hooks‬/custome-hooks/useFeatureAccess";
import { featureObject } from "helpers/privileges/utils";

const { REACT_APP_RAZORPAY_KEY } = process.env;
export const MAX_SEATS = 10000;
const PAYMENT_START_FAILED = "Couldn't start the payment. Please try again.";
const QUOTE_FAILED = "Couldn't work out the price. Please try again.";
const PAYMENT_NOT_CONFIRMED =
  "We couldn't confirm your payment. If you were charged, please contact support before retrying.";

const isRefused = (data: any) =>
  !!extractValidationErrorMessage(data) || data?.status === ERROR_STATUS_KEY;

const money = (value: any) =>
  Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Whole seats from 1 to MAX_SEATS, otherwise 0. */
export const seatCount = (value: string) => {
  const count = /^\d+$/.test(value.trim()) ? Number(value) : 0;
  return count >= 1 && count <= MAX_SEATS ? count : 0;
};

interface Props {
  subscription: any;
  openOnLoad?: boolean;
}

/**
 * Buys extra seats on the current plan. They end with the plan, and each is charged for the months
 * left on it, so nothing already paid for is charged again.
 */
const BuySeats = ({ subscription, openOnLoad }: Props) => {
  const { fetchSubcriptionDetail, userDetails, currentCompany } = useAuth();
  const { showSuccessToast, showErrorToast } = useCommonContext();
  // Ordering needs create and confirming the payment needs modify.
  const { canCreate, canModify } = useFeatureAccess(featureObject.SUBSCRIPTION);
  const canBuy = canCreate && canModify && subscription?.paymentStatus === "PD";
  const productName =
    subscription?.subscriptionPricingPlan?.subscriptionPlanProduct?.productType
      ?.displayName;
  const [seatPriceGuid, setSeatPriceGuid] = useState<string>();
  const [open, setOpen] = useState(false);
  const [seats, setSeats] = useState("1");
  const [quote, setQuote] = useState<any>();
  const [quoting, setQuoting] = useState(false);
  const [paying, setPaying] = useState(false);
  const count = seatCount(seats);

  useEffect(() => {
    if (!productName || !canBuy) return;
    let cancelled = false;
    getAvailableAddOns(productName)
      .then((response) => {
        const seatsAddOn = (response.data?.data ?? []).find(
          (feature: any) => feature.featureKey === "ADD_USERS",
        );
        const guid =
          seatsAddOn?.subscriptionFeatureAddOnPrice?.[0]
            ?.subscriptionFeatureAddOnPriceGuid;
        if (!cancelled) {
          setSeatPriceGuid(guid);
          if (guid && openOnLoad) setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [productName, openOnLoad, canBuy]);

  useEffect(() => {
    setQuote(undefined);
    if (!open || !seatPriceGuid || !count) return;
    let cancelled = false;
    setQuoting(true);
    const timer = setTimeout(() => {
      quoteAddOns([{ subscriptionFeatureAddOnPriceGuid: seatPriceGuid as string, count }])
        .then((response) => {
          if (cancelled) return;
          if (isRefused(response.data)) showErrorToast(QUOTE_FAILED);
          else setQuote(response.data?.data);
        })
        .catch(() => {
          if (!cancelled) showErrorToast(QUOTE_FAILED);
        })
        .finally(() => {
          if (!cancelled) setQuoting(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, seatPriceGuid, count, showErrorToast]);

  if (!seatPriceGuid || !canBuy) return null;

  const close = () => {
    if (paying) return;
    setOpen(false);
    setSeats("1");
  };

  const confirmPayment = (payment: any) =>
    confirmAddOnPayment(payment)
      .then((response) => {
        if (payment.status !== "SUCCESS") return;
        if (isRefused(response.data)) {
          showErrorToast(PAYMENT_NOT_CONFIRMED);
          return;
        }
        showSuccessToast(count === 1 ? "1 seat added" : `${count} seats added`);
        fetchSubcriptionDetail();
        setOpen(false);
        setSeats("1");
      })
      .catch(() => {
        if (payment.status === "SUCCESS") showErrorToast(PAYMENT_NOT_CONFIRMED);
      })
      .finally(() => setPaying(false));

  const payWithRazorpay = (order: any) => {
    const companySubscriptionGuid = String(order.receipt ?? "").replace(/^CSID_/, "");
    const razorpay = new (window as any).Razorpay({
      key: REACT_APP_RAZORPAY_KEY || "",
      amount: Math.round(Number(quote.totalAmount) * 100),
      currency: "INR",
      name: "Niyantras Bizro",
      order_id: order.order_id,
      description: count === 1 ? "1 more seat" : `${count} more seats`,
      handler: (response: any) =>
        confirmPayment({
          status: "SUCCESS",
          companySubscriptionGuid,
          orderId: response.razorpay_order_id,
          paymentId: response.razorpay_payment_id,
          signature: response.razorpay_signature,
        }),
      prefill: {
        name: `${userDetails?.firstName || ""} ${userDetails?.lastName || ""}`.trim() || currentCompany?.name,
        email: userDetails?.email,
        contact: userDetails?.mobileNumber || "",
      },
      theme: { color: "#4c39ae" },
      modal: { ondismiss: () => setPaying(false) },
    });
    // The order stays open after a failed attempt, so the payer can retry it in the same window.
    razorpay.on("payment.failed", (response: any) =>
      confirmPayment({
        status: "ERROR",
        companySubscriptionGuid,
        orderId: response.error?.metadata?.order_id || order.order_id,
        paymentId: response.error?.metadata?.payment_id || "",
        notes: response.error?.description,
      }),
    );
    razorpay.open();
  };

  const pay = () => {
    setPaying(true);
    orderAddOns(subscription.companySubscriptionGuid, [
      { subscriptionFeatureAddOnPriceGuid: seatPriceGuid as string, count },
    ])
      .then((response) => {
        if (isRefused(response.data) || !response.data?.order_id) {
          showErrorToast(PAYMENT_START_FAILED);
          setPaying(false);
          return;
        }
        if (quote?.currency?.currencyCode === "USD") {
          window.location.href = response.data.order_id;
          return;
        }
        payWithRazorpay(response.data);
      })
      .catch(() => {
        setPaying(false);
        showErrorToast(PAYMENT_START_FAILED);
      });
  };

  const line = quote?.subscriptionFeatureAddOnPriceDTO?.[0];
  const symbol = quote?.currency?.currencySymbol ?? "";
  const months = line?.months;
  const endDate = quote?.endDate ?? subscription?.endDate;

  return (
    <>
      <button type="button" className="ni-usage-plan-cta ni-usage-plan-cta--ghost" onClick={() => setOpen(true)}>
        Buy more seats
      </button>
      <Modal show={open} onHide={close} centered>
        <Modal.Header closeButton={!paying}>
          <Modal.Title>Buy more seats</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {endDate && (
            <p className="text-muted mb-4">
              New seats last until {moment(endDate).format("Do MMM YYYY")}, when your
              current plan ends. You pay only for the months left.
            </p>
          )}
          <label htmlFor="buy-seats-count" className="form-label fw-semibold">
            Seats to add
          </label>
          <input
            id="buy-seats-count"
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_SEATS}
            step={1}
            className={`form-control ${seats && !count ? "is-invalid" : ""}`}
            value={seats}
            disabled={paying}
            onChange={(e) => setSeats(e.target.value)}
          />
          {!count && (
            <div className="invalid-feedback d-block">
              Enter a whole number from 1 to {MAX_SEATS.toLocaleString()}.
            </div>
          )}

          <div className="mt-5" aria-live="polite">
            {quoting && <div className="text-muted">Working out the price...</div>}
            {!quoting && quote && (
              <table className="table table-sm mb-0">
                <tbody>
                  <tr>
                    <td>
                      {count} × {symbol}
                      {money(line?.featureUnitCost)}
                      {months
                        ? ` (${months} month${months === 1 ? "" : "s"} left)`
                        : ""}
                    </td>
                    <td className="text-end">
                      {symbol}
                      {money(quote.item?.basePlanAmount)}
                    </td>
                  </tr>
                  {(quote.item?.additionalCharges ?? []).map((charge: any) => (
                    <tr key={charge.displayName}>
                      <td>{charge.displayName}</td>
                      <td className="text-end">
                        {symbol}
                        {money(charge.price)}
                      </td>
                    </tr>
                  ))}
                  <tr className="fw-bold">
                    <td>Total</td>
                    <td className="text-end">
                      {symbol}
                      {money(quote.totalAmount)}
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
            {!quoting && quote && months ? (
              <div className="text-muted fs-7 mt-2">
                A part month is charged as a full month.
              </div>
            ) : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={close} disabled={paying}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary bg-primary-text"
            onClick={pay}
            disabled={!quote || quoting || paying || !count}
          >
            {paying && <span className="spinner-border spinner-border-sm align-middle me-2" />}
            {quote ? `Pay ${symbol}${money(quote.totalAmount)}` : "Pay"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default BuySeats;
